import React, { useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Heading from '@tiptap/extension-heading'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import BulletList from "@tiptap/extension-bullet-list"
import Image from '@tiptap/extension-image'
import History from '@tiptap/extension-history'
import { Button } from "@/components/ui/button"
import { Alert } from "@/components/ui/alert"
import { DirectUpload } from "@rails/activestorage"
import { useMutation } from "@tanstack/react-query";
import { getOEmbed } from "@/lib/api"
import OEmbedExtension from "@/components/ui/OEmbedExtension"

import {
  Wand2,
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List as BulletListIcon,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Upload,
  ImageUp,
  Maximize,
  Minimize
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { post } from "@rails/request.js"
const getExtensions = (isPlain) => {
  const baseExtensions = [
    Document,
    Paragraph,
    Text,
  ]

  if (isPlain) return baseExtensions

  return [
    ...baseExtensions,
    Bold,
    Italic,
    Link.configure({
      openOnClick: false,
    }),
    OrderedList,
    ListItem,
    BulletList,
    Image,
    History.configure({
      depth: 10,
    }),
    Heading.configure({
      levels: [1, 2, 3],
    }),
    OEmbedExtension, // Add oEmbed extension
  ]
}

export default function SimpleEditor({
  value,
  onChange,
  plain = false,
  scope = 'default',
  aiPromptContext = 'default'
}) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [aiDialogOpen, setAiDialogOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [isEnhancing, setIsEnhancing] = useState(false)

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef(null)

  // Image upload state
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null)

  // oEmbed state and mutation
  const [oembedDialogOpen, setOembedDialogOpen] = useState(false)
  const [oembedUrl, setOembedUrl] = useState('')
  const [oembedError, setOembedError] = useState(null)

  const oembedMutation = useMutation({
    mutationFn: getOEmbed,
    onError: (error) => {
      setOembedError(error.message || "Failed to fetch embed")
    },
    onSuccess: (data, url) => {
      debugger
      // Insert oEmbed node at cursor
      editor.chain().focus().insertContent({
        type: "oembed",
        attrs: {
          url,
          data,
        },
      }).run()
      setOembedDialogOpen(false)
      setOembedUrl('')
      setOembedError(null)
    },
  })

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadError(null)
    // Use DirectUpload to /direct_uploads
    const upload = new DirectUpload(file, "/direct_uploads")
    upload.create(async (error, blob) => {
      if (error) {
        setUploadError(error.message || "Upload failed")
        setUploading(false)
      } else {
        // Insert image at cursor using service_url
        // The controller returns { ..., service_url: "/rails/active_storage/blobs/...", ... }
        // We need to fetch the blob info to get the service_url
        // But DirectUpload does not return the controller response, so we fetch it manually
        try {
          // Fetch blob info from /rails/active_storage/blobs/:signed_id
          // But our controller already returns service_url, so we can construct it
          // The blob object has signed_id, but not service_url, so we need to fetch it
          // Instead, let's POST to /rails/active_storage/blobs/:signed_id or use the signed_id to get the URL
          // But our /direct_uploads returns service_url in the response, but DirectUpload does not expose it
          // So, after upload, we can fetch the blob info from /rails/active_storage/blobs/:signed_id
          // But that's not RESTful. Instead, let's POST to /rails/active_storage/blobs/:signed_id.json to get the URL
          // But for now, let's try to construct the URL as /rails/active_storage/blobs/redirect/:signed_id
          // Or, if the backend supports it, use /rails/active_storage/blobs/:signed_id
          // For now, insert the image using the signed_id as a fallback
          const imageUrl = `${window.location.protocol}//${window.location.host}/${blob.service_url}`
          editor.chain().focus().insertContent(`<img src="${imageUrl}" alt="Uploaded image" />`).run()
        } catch (err) {
          setUploadError("Failed to insert image")
        }
        setUploading(false)
      }
    })
  }

  const editor = useEditor({
    extensions: getExtensions(plain),
    content: value || '',
    editable: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  }, [])

  useEffect(() => {
    if (editor && value !== undefined) {
      const newContent = value || ''
      if (newContent !== editor.getHTML()) {
        editor.commands.setContent(newContent, false)
      }
    }
  }, [value, editor])

  // trigger onChange when editor is not in enhancing mode
  useEffect(() => {
    if (isEnhancing) return
    if (!editor) return
    onChange(editor.getHTML())
  }, [isEnhancing])

  const handleSetLink = (e) => {
    e.preventDefault()
    editor?.chain().focus().toggleLink({ href: linkUrl, target: '_blank' }).run()
    setLinkDialogOpen(false)
    setLinkUrl('')
  }

  const handleEnhanceWithAI = async (e) => {
    e.preventDefault()
    setIsEnhancing(true)

    try {
      const selectedText = editor?.state.selection.empty
        ? null
        : editor?.state.doc.textBetween(
          editor.state.selection.from,
          editor.state.selection.to
        )

      const textToEnhance = selectedText || value

      const promptWithContent = `
        ${aiPromptContext ? 'Context: ' + aiPromptContext : ''}
        ${aiPrompt}
      
      `

      const response = await post('/ai_enhancements/enhance.json', {
        body: JSON.stringify({
          text: textToEnhance,
          prompt: aiPrompt,
          scope: scope
        })
      })

      const data = await response.json

      if (response.ok) {
        if (selectedText) {
          editor?.chain().focus().deleteSelection().insertContent(data.enhanced_text).run()
        } else {
          editor?.chain().focus().setContent(data.enhanced_text).run()
        }
        setAiDialogOpen(false)
        setAiPrompt('')
      } else {
        console.error('Enhancement failed:', data.error)
      }
    } catch (error) {
      console.error('Enhancement request failed:', error)
    } finally {
      setIsEnhancing(false)
    }
  }

  if (!editor) return null

  // Fullscreen handlers
  const handleToggleFullscreen = () => {
    if (!containerRef.current) return
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen()
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen()
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen()
      }
      setIsFullscreen(true)
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
      setIsFullscreen(false)
    }
  }

  // Listen for fullscreen change to update state
  useEffect(() => {
    const handleChange = () => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement
      setIsFullscreen(!!fsElement)
    }
    document.addEventListener('fullscreenchange', handleChange)
    document.addEventListener('webkitfullscreenchange', handleChange)
    document.addEventListener('mozfullscreenchange', handleChange)
    document.addEventListener('MSFullscreenChange', handleChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleChange)
      document.removeEventListener('webkitfullscreenchange', handleChange)
      document.removeEventListener('mozfullscreenchange', handleChange)
      document.removeEventListener('MSFullscreenChange', handleChange)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={`border rounded-lg p-4 bg-background transition-all ${isFullscreen ? "fixed inset-0 z-50 flex flex-col max-w-none max-h-none w-screen h-screen" : ""}`}
    >

      <div className="flex items-center gap-2 mb-4 border-b pb-4 overflow-auto">
        {/* Fullscreen toggle button */}
        <Button
          size="sm"
          type="button"
          variant="outline"
          onClick={handleToggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <Minimize className="h-4 w-4" />
          ) : (
            <Maximize className="h-4 w-4" />
          )}
        </Button>
        {/* oEmbed block button */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOembedDialogOpen(true)}
          aria-label="Embed media"
          title="Embed media"
        >
          {/* Simple embed icon (can be replaced with a more specific one if desired) */}
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Button>
        {/* Image upload button */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          className="hidden"
          onChange={handleImageUpload}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
        >

          <ImageUp className="h-4 w-4" />
          {uploading && <span>...</span>}
        </Button>
        {uploadError && (
          <Alert variant="destructive" className="ml-2">
            {uploadError}
          </Alert>
        )}
        <Button
          size="sm"
          variant={editor.isActive('bold') ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()

            editor.chain().focus().toggleBold().run()
          }
          }
        >
          <BoldIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('italic') ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleItalic().run()
          }
          }
        >
          <ItalicIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          }
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          }
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          }
        >
          <Heading3 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('bulletList') ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleBulletList().run()
          }
          }
        >
          <BulletListIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('orderedList') ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            editor.chain().focus().toggleOrderedList().run()
          }
          }
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant={editor.isActive('link') ? 'default' : 'outline'}
          onClick={(e) => {
            e.preventDefault()
            setLinkDialogOpen(true)
          }
          }
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.preventDefault()
            setAiDialogOpen(true)
          }
          }
        >
          <Wand2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-[100px]- border rounded-md">
        <EditorContent editor={editor} className="prose dark:prose-invert max-w-none p-3" />
      </div>

      {/* oEmbed Dialog */}
      <Dialog open={oembedDialogOpen} onOpenChange={setOembedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Embed via oEmbed</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={e => {
              e.preventDefault()
              if (oembedUrl) {
                oembedMutation.mutate(oembedUrl)
              }
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="oembed-url">URL</Label>
              <Input
                id="oembed-url"
                value={oembedUrl}
                onChange={e => setOembedUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                disabled={oembedMutation.isLoading}
              />
            </div>
            {oembedMutation.isLoading && (
              <Skeleton className="h-8 w-full rounded" />
            )}
            {oembedError && (
              <Alert variant="destructive">{oembedError}</Alert>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={oembedMutation.isLoading || !oembedUrl}>
                Embed
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSetLink} className="space-y-4">
            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit">Add Link</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enhance with AI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="prompt">Prompt</Label>
              <Input
                id="prompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Enter your prompt..."
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleEnhanceWithAI} disabled={isEnhancing}>
                {isEnhancing ? (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'Enhance'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
